// src/components/Layout.js
import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SynergyLogo from './SynergyLogo';

const NAV_ADMIN = [
  { to: '/dashboard', label: 'Dashboard', icon: '▦' },
  { to: '/orders', label: 'Orders', icon: '◈' },
  { to: '/missing-agent', label: 'Missing agent', icon: '⚠' },
  { to: '/non-usage', label: 'Non-usage', icon: '◌' },
  { to: '/reps', label: 'Reps', icon: '◎' },
  { to: '/bulk-reps', label: 'Bulk upload reps', icon: '⊕' },
  { to: '/roster-import', label: 'Roster import', icon: '⇡' },
  { to: '/payroll', label: 'Payroll', icon: '$' },
  { to: '/bucket-release', label: 'Bucket release', icon: '◯' },
  { to: '/chargebacks', label: 'Chargebacks', icon: '↩' },
  { to: '/ytd-commissions', label: 'YTD commissions', icon: '↗' },
  { to: '/stack-ranking', label: 'Stack ranking', icon: '★' },
  { to: '/comp-plans', label: 'Comp plans', icon: '≡' },
  { to: '/seed-comp-plans', label: 'Seed comp plans', icon: '⚙' },
  { to: '/import', label: 'Import CSV', icon: '↑' },
  { to: '/claim-order', label: 'Claim order', icon: '✋' },
  { to: '/alerts', label: 'Alerts', icon: '◉' },
];

const NAV_REP = [
  { to: '/dashboard', label: 'My Dashboard', icon: '▦' },
  { to: '/my-orders', label: 'My Orders', icon: '◈' },
  { to: '/my-pay', label: 'My Pay', icon: '$' },
  { to: '/chargebacks', label: 'My Chargebacks', icon: '↩' },
  { to: '/claim-order', label: 'Claim order', icon: '✋' },
];

const MOBILE_BREAKPOINT = 768;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  useEffect(() => {
    function handleResize() { setIsMobile(window.innerWidth < MOBILE_BREAKPOINT); }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
}

export default function Layout({ children }) {
  const { userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = userProfile?.role === 'admin';
  const nav = isAdmin ? NAV_ADMIN : NAV_REP;
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar on navigation on mobile
  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile]);

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  return (
    <div style={styles.shell}>
      {/* Mobile top bar */}
      {isMobile && (
        <div style={styles.mobileTopBar}>
          <button style={styles.hamburger} onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? '✕' : '☰'}
          </button>
          <div style={styles.mobileBrand}>
            <SynergyLogo size={22} />
            <span style={styles.brandName}>SalesHub</span>
          </div>
          <div style={{ width: 36 }} />
        </div>
      )}

      {/* Overlay for mobile */}
      {isMobile && sidebarOpen && (
        <div style={styles.overlay} onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside style={{
        ...styles.sidebar,
        ...(isMobile ? {
          ...styles.sidebarMobile,
          transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
        } : {}),
      }}>
        {!isMobile && (
          <div style={styles.brand}>
            <SynergyLogo size={24} />
            <span style={styles.brandName}>SalesHub</span>
          </div>
        )}
        {isAdmin && (
          <div style={styles.orgBadge}>
            <span style={styles.orgDot} />
            Spectrum
          </div>
        )}
        <nav style={styles.nav}>
          {nav.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              style={({ isActive }) => ({
                ...styles.navItem,
                ...(isActive ? styles.navItemActive : {}),
              })}
            >
              <span style={styles.navIcon}>{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div style={styles.sidebarFooter}>
          <div style={styles.userInfo}>
            <div style={styles.avatar}>
              {(userProfile?.name || 'U')[0].toUpperCase()}
            </div>
            <div>
              <div style={styles.userName}>{userProfile?.name || 'User'}</div>
              <div style={styles.userRole}>{userProfile?.role || 'agent'}</div>
            </div>
          </div>
          <button style={styles.logoutBtn} onClick={handleLogout}>Sign out</button>
        </div>
      </aside>

      {/* Main content */}
      <main style={{
        ...styles.main,
        ...(isMobile ? styles.mainMobile : {}),
      }}>
        {children}
      </main>
    </div>
  );
}

const styles = {
  shell: {
    display: 'flex',
    minHeight: '100vh',
    background: '#0a0a0f',
    fontFamily: "'DM Sans', sans-serif",
  },
  // Mobile top bar
  mobileTopBar: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: 54,
    background: '#13131a',
    borderBottom: '1px solid #1e1e2a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 16px',
    zIndex: 1001,
  },
  hamburger: {
    background: 'transparent',
    border: 'none',
    color: '#fff',
    fontSize: 22,
    cursor: 'pointer',
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileBrand: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.6)',
    zIndex: 1002,
  },
  // Sidebar
  sidebar: {
    width: 220,
    minHeight: '100vh',
    background: '#13131a',
    borderRight: '1px solid #1e1e2a',
    display: 'flex',
    flexDirection: 'column',
    padding: '24px 0',
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 1003,
  },
  sidebarMobile: {
    top: 0,
    transition: 'transform 0.25s ease',
    boxShadow: '4px 0 20px rgba(0,0,0,0.5)',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '0 20px 20px',
    borderBottom: '1px solid #1e1e2a',
  },
  brandName: {
    fontSize: 16,
    fontWeight: 600,
    color: '#fff',
    letterSpacing: '-0.2px',
  },
  orgBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    margin: '12px 20px',
    fontSize: 11,
    color: '#666',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  orgDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#00d4a0',
    display: 'inline-block',
  },
  nav: {
    flex: 1,
    padding: '8px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    overflowY: 'auto',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '9px 12px',
    borderRadius: 8,
    color: '#666',
    textDecoration: 'none',
    fontSize: 13,
    fontWeight: 500,
    transition: 'all .15s',
  },
  navItemActive: {
    background: '#1a1a28',
    color: '#00d4a0',
  },
  navIcon: {
    fontSize: 14,
    width: 18,
    textAlign: 'center',
  },
  sidebarFooter: {
    padding: '16px 20px 0',
    borderTop: '1px solid #1e1e2a',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: '#1e2a24',
    border: '1px solid #00d4a0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 600,
    color: '#00d4a0',
  },
  userName: { fontSize: 13, color: '#ddd', fontWeight: 500 },
  userRole: { fontSize: 11, color: '#555', textTransform: 'capitalize' },
  logoutBtn: {
    width: '100%',
    background: 'transparent',
    border: '1px solid #2a2a3a',
    borderRadius: 7,
    padding: '7px',
    color: '#555',
    fontSize: 12,
    cursor: 'pointer',
    transition: 'all .15s',
  },
  // Main content
  main: {
    flex: 1,
    marginLeft: 220,
    minHeight: '100vh',
  },
  mainMobile: {
    marginLeft: 0,
    paddingTop: 54,
  },
};
